// src/context/AuthContext.jsx - The Art of Story auth (via the central Auth Hub)
//
// Sign-in is delegated to the DDD Auth Hub (auth.divedeepdialogues.com): every
// Dive Deep Dialogues app signs in there, so there is one account and one
// place to delete it. The `user` here is the REAL ddd-art-of-story
// (spoke) Firebase session that /auth/callback establishes, the same session
// the Firestore rules and the Knowledge Hub check.
//
// localStorage mirrors the session for the storage-preference features, but it
// is never the source of truth. It used to be: a user was rebuilt from the
// "artOfStory_isSignedIn" flags before Firebase answered, so setting two
// keys in devtools made the app believe you were signed in (September 2026
// audit, §11). Only onAuthStateChanged decides now.
//
// Setting VITE_USE_AUTH_HUB=false restores the old sign-in page
// (/auth-art-of-story.html) as a rollback.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { authHubService } from '../services/authHub';
import { clearCloudData as clearCloudDataService } from '../services/storageService';

const AuthContext = createContext({});

// eslint-disable-next-line react-refresh/only-export-components -- hook shares the provider's context
export const useAuth = () => useContext(AuthContext);

const LS_PREFIX = 'artOfStory_';
const MIRROR_KEYS = ['isSignedIn', 'userId', 'userEmail', 'displayName', 'authType'];

const clearMirror = () => {
  try {
    MIRROR_KEYS.forEach((k) => localStorage.removeItem(`${LS_PREFIX}${k}`));
  } catch { /* localStorage unavailable */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storagePreference, setStoragePreference] = useState('both'); // 'local', 'cloud', 'both'

  useEffect(() => {
    try {
      const savedPreference = localStorage.getItem(`${LS_PREFIX}storagePreference`);
      if (savedPreference) setStoragePreference(savedPreference);
    } catch { /* localStorage unavailable */ }

    // The old sign-in page returns with ?auth=success; the session itself
    // arrives through onAuthStateChanged, so just tidy the address bar.
    if (new URLSearchParams(window.location.search).get('auth') === 'success') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          authType: firebaseUser.isAnonymous
            ? 'anonymous'
            : firebaseUser.providerData[0]?.providerId?.includes('google') ? 'google' : 'email',
          isAnonymous: firebaseUser.isAnonymous,
        };
        setUser(userData);

        // Mirror to localStorage when the preference allows.
        try {
          const pref = localStorage.getItem(`${LS_PREFIX}storagePreference`) || 'both';
          if (pref === 'local' || pref === 'both') {
            localStorage.setItem(`${LS_PREFIX}isSignedIn`, 'true');
            localStorage.setItem(`${LS_PREFIX}userId`, userData.uid);
            localStorage.setItem(`${LS_PREFIX}userEmail`, userData.email || '');
            localStorage.setItem(`${LS_PREFIX}displayName`, userData.displayName);
            localStorage.setItem(`${LS_PREFIX}authType`, userData.authType);
          }
        } catch { /* localStorage unavailable */ }
      } else {
        setUser(null);
        clearMirror();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign-in and sign-up go to the Auth Hub. `action` keeps the old signature
  // (callers pass 'signin'); 'signup' opens the hub's sign-up page. The current
  // path rides along so the person lands back where they started.
  const redirectToAuth = (action = 'signin') => {
    if (!authHubService.isEnabled()) {
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `/auth-art-of-story.html?return_url=${returnUrl}#${action}`;
      return;
    }
    const redirectPath = window.location.pathname + window.location.search;
    if (action === 'signup') authHubService.redirectToSignup(redirectPath);
    else authHubService.redirectToLogin(redirectPath);
  };

  const logOut = async () => {
    try {
      // Both sessions (this app's and the hub's); the hub's own sign-in page
      // stays signed in, as in the other DDD apps.
      await authHubService.signOut();
      clearMirror();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateStoragePreference = (preference) => {
    setStoragePreference(preference);
    try {
      localStorage.setItem(`${LS_PREFIX}storagePreference`, preference);
    } catch { /* localStorage unavailable */ }
  };

  const clearLocalData = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(LS_PREFIX)) localStorage.removeItem(key);
      });
    } catch { /* localStorage unavailable */ }
  };

  const clearCloudData = async () => {
    if (!user) return;
    try {
      // Clear Firestore data for this user using storage service
      await clearCloudDataService(user.uid);
    } catch (error) {
      console.error('❌ Error clearing cloud data:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAnonymous: user?.isAnonymous || false,
    displayName: user?.displayName || 'Guest',

    // Storage preferences
    storagePreference,
    updateStoragePreference,
    clearLocalData,
    clearCloudData,
    usesLocal: storagePreference === 'local' || storagePreference === 'both',
    usesCloud: storagePreference === 'cloud' || storagePreference === 'both',

    // Auth actions
    redirectToAuth,
    logOut,

    // Firebase instances (for services that need them)
    auth,
    db,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
